import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { entryStats } from '@/lib/sportStats';
import { toast } from 'sonner';
import { Loader2, X, Plus, Trash2, Save, ClipboardList, ScanLine } from 'lucide-react';
import { ScanBoxScoreDialog } from './ScanBoxScoreDialog';
import { isCricket, cricketSideError } from '@/lib/cricket';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sport-aware box-score entry modal for non-basketball events (soccer,
 * volleyball). Renders one row per player with a numeric input for each
 * sport-specific stat. Auto-computes the team's final score for sports
 * that expose a ``team_score_field`` (e.g. sum of goals for soccer).
 */
export function BoxScoreEntryModal({ open, onClose, event, match, sportDefinition, onSaved }) {
  const [side, setSide] = useState('a');
  const [teamA, setTeamA] = useState({ players: [], final_score: null });
  const [teamB, setTeamB] = useState({ players: [], final_score: null });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [advancedOn, setAdvancedOn] = useState(!!event?.advanced_stats);
  useEffect(() => { setAdvancedOn(!!event?.advanced_stats); }, [event?.advanced_stats]);

  const stats = entryStats(sportDefinition, advancedOn);
  const advancedLabels = (sportDefinition?.stats || []).filter((s) => s.advanced).map((s) => s.label);
  const scoreField = sportDefinition?.team_score_field || null;
  const cricket = isCricket(event?.sport);
  const [extras, setExtras] = useState({});
  useEffect(() => {
    if (!open || !match) return;
    const s = (v) => (v === null || v === undefined ? '' : String(v));
    setExtras({ wa: s(match.wickets_a), wb: s(match.wickets_b), oa: s(match.overs_a), ob: s(match.overs_b) });
  }, [open, match]);

  const seedRows = useCallback((existing, matchSide) => {
    if (existing && existing.players && existing.players.length) {
      return { players: existing.players, final_score: existing.final_score ?? null };
    }
    // Seed with a single blank row so the user always sees something to fill.
    return { players: [{ name: '', jersey_number: '', stats: {} }], final_score: null };
  }, []);

  const fetchExisting = useCallback(async () => {
    if (!open || !event?.id || !match?.id) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(
        `${API_URL}/api/events/${event.id}/matches/${match.id}/box-score-entry`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error('Failed to load saved stats');
      const data = await res.json();
      setTeamA(seedRows(data.entries?.a, 'a'));
      setTeamB(seedRows(data.entries?.b, 'b'));
    } catch (e) {
      toast.error(e.message || 'Failed to load saved stats');
      setTeamA(seedRows(null, 'a'));
      setTeamB(seedRows(null, 'b'));
    } finally {
      setLoading(false);
    }
  }, [open, event?.id, match?.id, seedRows]);

  useEffect(() => { fetchExisting(); }, [fetchExisting]);

  if (!open) return null;

  const teamState = side === 'a' ? teamA : teamB;
  const setTeamState = side === 'a' ? setTeamA : setTeamB;
  const teamName = side === 'a' ? match?.team_a_name : match?.team_b_name;

  const computedScore = scoreField
    ? teamState.players.reduce((sum, p) => sum + (parseInt(p.stats?.[scoreField], 10) || 0), 0)
    : (teamState.final_score ?? '');

  const setPlayerField = (idx, field, value) => {
    setTeamState((prev) => {
      const next = { ...prev, players: [...prev.players] };
      next.players[idx] = { ...next.players[idx], [field]: value };
      return next;
    });
  };

  const setPlayerStat = (idx, statKey, rawValue) => {
    setTeamState((prev) => {
      const next = { ...prev, players: [...prev.players] };
      const iv = rawValue === '' ? '' : Math.max(0, parseInt(rawValue, 10) || 0);
      next.players[idx] = {
        ...next.players[idx],
        stats: { ...(next.players[idx].stats || {}), [statKey]: iv },
      };
      return next;
    });
  };

  const toggleAdvanced = async (on) => {
    setAdvancedOn(on);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API_URL}/api/events/${event.id}/advanced-stats`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: on }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Could not update stats');
      onSaved && onSaved();
    } catch (e) {
      setAdvancedOn(!on);
      toast.error(e.message || 'Could not update stats');
    }
  };

  const addPlayer = () => setTeamState((p) => ({ ...p, players: [...p.players, { name: '', jersey_number: '', stats: {} }] }));
  const removePlayer = (idx) => setTeamState((p) => ({ ...p, players: p.players.filter((_, i) => i !== idx) }));

  const applyScan = (found) => {
    const seed = (bucket) => ({
      players: bucket.players.map((p) => ({ name: p.name || '', jersey_number: p.jersey_number || '', stats: p.stats || {} })),
      final_score: null,
    });
    if (found.a) setTeamA(seed(found.a));
    if (found.b) setTeamB(seed(found.b));
    if (!found.a && found.b) setSide('b');
  };

  const handleSave = async () => {
    // Filter blank rows
    const players = teamState.players
      .filter((p) => (p.name || '').trim())
      .map((p) => ({
        player_id: p.player_id,
        name: p.name.trim(),
        jersey_number: (p.jersey_number || '').toString(),
        stats: Object.fromEntries(
          Object.entries(p.stats || {})
            .filter(([, v]) => v !== '' && v !== null && v !== undefined)
            .map(([k, v]) => [k, Math.max(0, parseInt(v, 10) || 0)]),
        ),
      }));

    if (players.length === 0 && (teamState.final_score === null || teamState.final_score === '')) {
      toast.error('Add at least one player or enter a final score.');
      return;
    }
    const wKey = side === 'a' ? 'wa' : 'wb';
    const oKey = side === 'a' ? 'oa' : 'ob';
    if (cricket) {
      const err = cricketSideError(teamName || 'this team', extras[wKey], extras[oKey]);
      if (err) { toast.error(err); return; }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        team_side: side,
        players,
        final_score: scoreField ? null : (teamState.final_score === '' ? null : parseInt(teamState.final_score, 10)),
        ...(cricket ? { wickets: parseInt(extras[wKey], 10), overs: String(extras[oKey] || '').trim() || null } : {}),
      };
      const res = await fetch(
        `${API_URL}/api/events/${event.id}/matches/${match.id}/box-score-entry`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      toast.success(`Saved ${teamName || 'team'} box score`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="box-score-entry-modal">
      <Card className={`w-full ${stats.length > 7 ? 'max-w-6xl' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto border-slate-200 shadow-xl`}>
        <CardHeader className="flex flex-row items-center justify-between pb-2 sticky top-0 bg-white z-10 border-b border-slate-100">
          <CardTitle className="text-xl font-bold text-[#00247D] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <ClipboardList className="w-5 h-5" /> BOX SCORE — {(sportDefinition?.label || event?.sport || '').toUpperCase()}
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="box-score-close">
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <p className="text-xs text-slate-500">
            {match?.team_a_name || 'Team A'} vs {match?.team_b_name || 'Team B'}
            {match?.date ? ` • ${match.date}` : ''}
          </p>

          <Tabs value={side} onValueChange={setSide}>
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="a" data-testid="box-score-tab-a">{match?.team_a_name || 'Team A'}</TabsTrigger>
              <TabsTrigger value="b" data-testid="box-score-tab-b">{match?.team_b_name || 'Team B'}</TabsTrigger>
            </TabsList>
          </Tabs>

          {loading ? (
            <div className="py-12 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {/* Team-level score */}
              <div className="bg-slate-50 border border-slate-100 rounded-md p-3 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  <span className="text-slate-500">{cricket ? 'Final score (runs / wickets, overs optional) for ' : 'Final score for '}</span>
                  <span className="font-medium text-slate-800">{teamName || (side === 'a' ? 'Team A' : 'Team B')}</span>
                </div>
                {scoreField ? (
                  <div className="text-2xl font-bold text-[#00247D]" data-testid="box-score-final-score">{computedScore}</div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <Input
                      type="number"
                      min="0"
                      className={cricket ? 'w-20 text-center' : 'w-24 text-right'}
                      value={teamState.final_score ?? ''}
                      onChange={(e) => setTeamState((p) => ({ ...p, final_score: e.target.value }))}
                      placeholder={cricket ? 'Runs' : '0'}
                      data-testid="box-score-final-score-input"
                    />
                    {cricket && (
                      <>
                        <span className="text-slate-400 font-bold">/</span>
                        <Input type="number" min="0" max="10" className="w-20 text-center" placeholder="Wkts"
                          value={extras[side === 'a' ? 'wa' : 'wb'] ?? ''}
                          onChange={(e) => setExtras((x) => ({ ...x, [side === 'a' ? 'wa' : 'wb']: e.target.value }))}
                          data-testid="box-score-wickets-input" />
                        <Input inputMode="decimal" className="w-20 text-center" placeholder="Overs"
                          value={extras[side === 'a' ? 'oa' : 'ob'] ?? ''}
                          onChange={(e) => setExtras((x) => ({ ...x, [side === 'a' ? 'oa' : 'ob']: e.target.value }))}
                          data-testid="box-score-overs-input" title="Optional, e.g. 18.4" />
                      </>
                    )}
                  </div>
                )}
              </div>

              {advancedLabels.length > 0 && (
                <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2" data-testid="bs-advanced-row">
                  <div className="text-sm">
                    <p className="font-medium text-slate-800">Track yards &amp; passing TDs</p>
                    <p className="text-xs text-slate-500">Optional. Adds {advancedLabels.join(', ')} for every game in this league.</p>
                  </div>
                  <Switch checked={advancedOn} onCheckedChange={toggleAdvanced} data-testid="bs-advanced-toggle" />
                </div>
              )}

              {/* Player rows */}
              <div className="overflow-x-auto">
                {stats.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4">
                    This sport only tracks the final score above — no per-player stats. Click Save.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                        <th className="py-2 pr-2 font-medium">#</th>
                        <th className="py-2 pr-2 font-medium">Player</th>
                        {stats.map((s) => (
                          <th key={s.key} className="py-2 pr-2 font-medium text-center" title={s.label}>{s.label}</th>
                        ))}
                        <th className="py-2 pr-0"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {teamState.players.map((p, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-2 pr-2">
                            <Input
                              className="h-8 w-14"
                              value={p.jersey_number || ''}
                              onChange={(e) => setPlayerField(i, 'jersey_number', e.target.value)}
                              placeholder="#"
                              data-testid={`bs-player-${side}-${i}-jersey`}
                            />
                          </td>
                          <td className="py-2 pr-2">
                            <Input
                              className="h-8 min-w-[130px]"
                              value={p.name || ''}
                              onChange={(e) => setPlayerField(i, 'name', e.target.value)}
                              placeholder="Player name"
                              data-testid={`bs-player-${side}-${i}-name`}
                            />
                          </td>
                          {stats.map((s) => (
                            <td key={s.key} className="py-2 pr-2 text-center">
                              <Input
                                type="number"
                                min="0"
                                className="h-8 w-16 text-center"
                                value={p.stats?.[s.key] ?? ''}
                                onChange={(e) => setPlayerStat(i, s.key, e.target.value)}
                                data-testid={`bs-player-${side}-${i}-${s.key}`}
                              />
                            </td>
                          ))}
                          <td className="py-2 pr-0 text-right">
                            <button
                              type="button"
                              onClick={() => removePlayer(i)}
                              className="text-slate-400 hover:text-red-500 p-1"
                              title="Remove player"
                              data-testid={`bs-remove-player-${side}-${i}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {stats.length > 0 && (
                <div>
                  <Button variant="outline" size="sm" onClick={addPlayer} data-testid={`bs-add-player-${side}`}>
                    <Plus className="w-4 h-4 mr-1" /> Add Player
                  </Button>
                </div>
              )}

              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t border-slate-100">
                <Button variant="outline" onClick={onClose} disabled={saving} data-testid="bs-close-btn">Close</Button>
                {stats.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setScanOpen(true)}
                    disabled={saving}
                    className="border-amber-400 text-amber-700 hover:bg-amber-50"
                    data-testid="bs-scan-btn"
                    title="AI reads a photo or PDF of the paper stat sheet"
                  >
                    <ScanLine className="w-4 h-4 mr-2" />Scan Box Score
                  </Button>
                )}
                <Button onClick={handleSave} disabled={saving} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="bs-save-btn">
                  {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>) : (<><Save className="w-4 h-4 mr-2" />Save {teamName || 'Team'}</>)}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <ScanBoxScoreDialog open={scanOpen} onOpenChange={setScanOpen} event={event} match={match} onScanned={applyScan} />
    </div>
  );
}
